import {
  parsePersonalFilterModes,
  parsePersonalFilterPreferences,
  type PersonalFilterMode,
  type PersonalFilterModes,
  type PersonalFilterPreferences,
} from "./personal-filters";
import {
  parseShoppingList,
  serializeShoppingList,
  type ShoppingListItem,
} from "./shopping-list";
import type { GridMode, SortMode } from "./types";
import {
  normalizeAppLocale,
  type AppLocale,
} from "./locale";
import {
  normalizeThemePreference,
  type ThemePreference,
} from "./theme";
import { normalizeStoreSavePromptNextEligibleAt } from "./store-save-prompt";

export const USER_STATE_VERSION = 1;
export const MAX_SYNCED_SHOPPING_ITEMS = 300;

const SORT_MODES: readonly SortMode[] = [
  "default",
  "price-asc",
  "discount-desc",
  "catalog-order",
];

export interface UserFilterSelection {
  store: string;
  categoryId: string;
}

export interface UserState {
  version: 1;
  shoppingItems: ShoppingListItem[];
  personalFilters: PersonalFilterPreferences;
  personalFilterModes: PersonalFilterModes;
  filterSelection: UserFilterSelection;
  gridMode: GridMode;
  sort: SortMode;
  locale: AppLocale | null;
  themePreference: ThemePreference;
  storeSavePromptNextEligibleAt: number;
  fuelVisited: boolean;
  priceWatchDefaultStores: PersonalFilterMode;
}

export interface ShoppingStateMutation {
  upsert?: ShoppingListItem[];
  remove?: string[];
}

export interface UserStateMutation {
  shopping?: ShoppingStateMutation;
  personalFilters?: Partial<PersonalFilterPreferences> & {
    /** Compatibility with mutations sent by an older cached Mini App. */
    catalogNotifications?: boolean;
  };
  personalFilterModes?: Partial<PersonalFilterModes>;
  filterSelection?: Partial<UserFilterSelection>;
  gridMode?: GridMode;
  sort?: SortMode;
  locale?: AppLocale;
  themePreference?: ThemePreference;
  storeSavePromptNextEligibleAt?: number;
  /** Once true, an older device cannot reset discovery. */
  fuelVisited?: true;
  priceWatchDefaultStores?: PersonalFilterMode;
}

export function defaultUserState(): UserState {
  return {
    version: USER_STATE_VERSION,
    shoppingItems: [],
    personalFilters: {
      stores: [],
      categoryIds: [],
      catalogNotificationStores: [],
    },
    personalFilterModes: { stores: "all", categories: "all" },
    filterSelection: { store: "all", categoryId: "all" },
    gridMode: "compact",
    sort: "default",
    locale: null,
    themePreference: "auto",
    storeSavePromptNextEligibleAt: 0,
    fuelVisited: false,
    priceWatchDefaultStores: "all",
  };
}

function boundedString(value: unknown, fallback: string): string {
  return typeof value === "string" && value.trim() && value.length <= 160
    ? value.trim()
    : fallback;
}

function cleanKeys(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const keys: string[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    if (
      typeof item !== "string" ||
      !item ||
      item.length > 256 ||
      seen.has(item)
    ) {
      continue;
    }
    seen.add(item);
    keys.push(item);
    if (keys.length >= MAX_SYNCED_SHOPPING_ITEMS) {
      break;
    }
  }
  return keys;
}

function normalizeShoppingItems(value: unknown): ShoppingListItem[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return parseShoppingList(
    JSON.stringify({ version: 1, items: value }),
  ).slice(0, MAX_SYNCED_SHOPPING_ITEMS);
}

function normalizeSort(value: unknown): SortMode {
  return SORT_MODES.includes(value as SortMode)
    ? (value as SortMode)
    : "default";
}

function normalizeGridMode(value: unknown): GridMode {
  return value === "single" ? "single" : "compact";
}

export function normalizeUserState(value: unknown): UserState | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const candidate = value as Record<string, unknown>;
  if (candidate.version !== USER_STATE_VERSION) {
    return null;
  }
  const personalFilters = parsePersonalFilterPreferences(
    JSON.stringify(candidate.personalFilters ?? {}),
  );
  const personalFilterModes = parsePersonalFilterModes(
    JSON.stringify(candidate.personalFilterModes ?? {}),
  );
  const rawSelection =
    candidate.filterSelection &&
    typeof candidate.filterSelection === "object" &&
    !Array.isArray(candidate.filterSelection)
      ? (candidate.filterSelection as Record<string, unknown>)
      : {};
  return {
    version: USER_STATE_VERSION,
    shoppingItems: normalizeShoppingItems(candidate.shoppingItems),
    personalFilters,
    personalFilterModes,
    filterSelection: {
      store: boundedString(rawSelection.store, "all"),
      categoryId: boundedString(rawSelection.categoryId, "all"),
    },
    gridMode: normalizeGridMode(candidate.gridMode),
    sort: normalizeSort(candidate.sort),
    locale: normalizeAppLocale(candidate.locale),
    themePreference: normalizeThemePreference(candidate.themePreference) ?? "auto",
    fuelVisited: candidate.fuelVisited === true,
    priceWatchDefaultStores: candidate.priceWatchDefaultStores === "personal" ? "personal" : "all",
    storeSavePromptNextEligibleAt: normalizeStoreSavePromptNextEligibleAt(
      candidate.storeSavePromptNextEligibleAt,
    ),
  };
}

export function parseStoredUserState(payload: string): UserState | null {
  try {
    return normalizeUserState(JSON.parse(payload));
  } catch {
    return null;
  }
}

export function serializeUserState(state: UserState): string {
  const normalized = normalizeUserState(state) ?? defaultUserState();
  return JSON.stringify(normalized);
}

function normalizeFilterMode(value: unknown): PersonalFilterMode | undefined {
  return value === "all" || value === "personal" ? value : undefined;
}

function optionalCleanValues(value: unknown): string[] | undefined {
  if (value === undefined) {
    return undefined;
  }
  const parsed = parsePersonalFilterPreferences(
    JSON.stringify({ stores: value, categoryIds: value }),
  );
  return parsed.stores;
}

export function normalizeUserStateMutation(
  value: unknown,
): UserStateMutation | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const candidate = value as Record<string, unknown>;
  const mutation: UserStateMutation = {};

  if (candidate.shopping !== undefined) {
    if (
      !candidate.shopping ||
      typeof candidate.shopping !== "object" ||
      Array.isArray(candidate.shopping)
    ) {
      return null;
    }
    const shopping = candidate.shopping as Record<string, unknown>;
    mutation.shopping = {
      upsert:
        shopping.upsert === undefined
          ? undefined
          : normalizeShoppingItems(shopping.upsert),
      remove:
        shopping.remove === undefined
          ? undefined
          : cleanKeys(shopping.remove),
    };
  }

  if (candidate.personalFilters !== undefined) {
    if (
      !candidate.personalFilters ||
      typeof candidate.personalFilters !== "object" ||
      Array.isArray(candidate.personalFilters)
    ) {
      return null;
    }
    const personalFilters = candidate.personalFilters as Record<string, unknown>;
    const stores = optionalCleanValues(personalFilters.stores);
    const categoryIds = optionalCleanValues(personalFilters.categoryIds);
    const catalogNotificationStores = optionalCleanValues(
      personalFilters.catalogNotificationStores,
    );
    const legacyCatalogNotifications =
      personalFilters.catalogNotifications === undefined
        ? undefined
        : personalFilters.catalogNotifications !== false;
    mutation.personalFilters = {
      stores,
      categoryIds,
      catalogNotificationStores,
      catalogNotifications:
        catalogNotificationStores === undefined
          ? legacyCatalogNotifications
          : undefined,
    };
  }

  if (candidate.personalFilterModes !== undefined) {
    if (
      !candidate.personalFilterModes ||
      typeof candidate.personalFilterModes !== "object" ||
      Array.isArray(candidate.personalFilterModes)
    ) {
      return null;
    }
    const modes = candidate.personalFilterModes as Record<string, unknown>;
    mutation.personalFilterModes = {
      stores: normalizeFilterMode(modes.stores),
      categories: normalizeFilterMode(modes.categories),
    };
  }

  if (candidate.filterSelection !== undefined) {
    if (
      !candidate.filterSelection ||
      typeof candidate.filterSelection !== "object" ||
      Array.isArray(candidate.filterSelection)
    ) {
      return null;
    }
    const selection = candidate.filterSelection as Record<string, unknown>;
    mutation.filterSelection = {
      store:
        selection.store === undefined
          ? undefined
          : boundedString(selection.store, "all"),
      categoryId:
        selection.categoryId === undefined
          ? undefined
          : boundedString(selection.categoryId, "all"),
    };
  }

  if (candidate.gridMode !== undefined) {
    if (candidate.gridMode !== "single" && candidate.gridMode !== "compact") {
      return null;
    }
    mutation.gridMode = candidate.gridMode;
  }
  if (candidate.sort !== undefined) {
    if (!SORT_MODES.includes(candidate.sort as SortMode)) {
      return null;
    }
    mutation.sort = candidate.sort as SortMode;
  }
  if (candidate.locale !== undefined) {
    const locale = normalizeAppLocale(candidate.locale);
    if (!locale) {
      return null;
    }
    mutation.locale = locale;
  }
  if (candidate.themePreference !== undefined) {
    const themePreference = normalizeThemePreference(candidate.themePreference);
    if (!themePreference) {
      return null;
    }
    mutation.themePreference = themePreference;
  }
  if (candidate.storeSavePromptNextEligibleAt !== undefined) {
    const nextEligibleAt = normalizeStoreSavePromptNextEligibleAt(
      candidate.storeSavePromptNextEligibleAt,
    );
    if (!nextEligibleAt) {
      return null;
    }
    mutation.storeSavePromptNextEligibleAt = nextEligibleAt;
  }
  if (candidate.fuelVisited === true) mutation.fuelVisited = true;
  if (candidate.priceWatchDefaultStores !== undefined) {
    const scope = normalizeFilterMode(candidate.priceWatchDefaultStores);
    if (!scope) return null;
    mutation.priceWatchDefaultStores = scope;
  }
  return mutation;
}

export function applyUserStateMutation(
  state: UserState,
  mutation: UserStateMutation,
): UserState {
  const next = normalizeUserState(state) ?? defaultUserState();
  if (mutation.shopping) {
    const items = new Map(
      next.shoppingItems.map((item) => [item.key, { ...item }] as const),
    );
    for (const key of mutation.shopping.remove ?? []) {
      items.delete(key);
    }
    for (const item of mutation.shopping.upsert ?? []) {
      items.set(item.key, { ...item });
    }
    next.shoppingItems = [...items.values()].slice(
      0,
      MAX_SYNCED_SHOPPING_ITEMS,
    );
  }
  if (mutation.personalFilters) {
    const stores =
      mutation.personalFilters.stores ?? next.personalFilters.stores;
    const selectedStores = new Set(stores);
    const requestedNotificationStores =
      mutation.personalFilters.catalogNotificationStores ??
      (mutation.personalFilters.catalogNotifications === undefined
        ? next.personalFilters.catalogNotificationStores
        : mutation.personalFilters.catalogNotifications
          ? stores
          : []);
    next.personalFilters = {
      stores,
      categoryIds:
        mutation.personalFilters.categoryIds ??
        next.personalFilters.categoryIds,
      catalogNotificationStores: requestedNotificationStores.filter(
        (store) => selectedStores.has(store),
      ),
    };
  }
  if (mutation.personalFilterModes) {
    next.personalFilterModes = {
      stores:
        mutation.personalFilterModes.stores ??
        next.personalFilterModes.stores,
      categories:
        mutation.personalFilterModes.categories ??
        next.personalFilterModes.categories,
    };
  }
  if (mutation.filterSelection) {
    next.filterSelection = {
      store: mutation.filterSelection.store ?? next.filterSelection.store,
      categoryId:
        mutation.filterSelection.categoryId ?? next.filterSelection.categoryId,
    };
  }
  if (mutation.gridMode) {
    next.gridMode = mutation.gridMode;
  }
  if (mutation.sort) {
    next.sort = mutation.sort;
  }
  if (mutation.locale) {
    next.locale = mutation.locale;
  }
  if (mutation.themePreference) {
    next.themePreference = mutation.themePreference;
  }
  if (mutation.fuelVisited === true) next.fuelVisited = true;
  if (mutation.priceWatchDefaultStores !== undefined) {
    next.priceWatchDefaultStores = mutation.priceWatchDefaultStores;
  }
  if (mutation.storeSavePromptNextEligibleAt !== undefined) {
    next.storeSavePromptNextEligibleAt = Math.max(
      next.storeSavePromptNextEligibleAt,
      mutation.storeSavePromptNextEligibleAt,
    );
  }
  return next;
}

function uniqueValues(left: string[], right: string[]): string[] {
  return [...new Set([...left, ...right])].slice(0, 64);
}

export function mergeBootstrapUserState(
  server: UserState,
  local: UserState,
): UserState {
  const serverState = normalizeUserState(server) ?? defaultUserState();
  const localState = normalizeUserState(local) ?? defaultUserState();
  const items = new Map(
    serverState.shoppingItems.map((item) => [item.key, item] as const),
  );
  for (const item of localState.shoppingItems) {
    if (!items.has(item.key)) {
      items.set(item.key, item);
    }
  }
  const stores = uniqueValues(
    serverState.personalFilters.stores,
    localState.personalFilters.stores,
  );
  const serverStores = new Set(serverState.personalFilters.stores);
  const catalogNotificationStores = uniqueValues(
    serverState.personalFilters.catalogNotificationStores,
    localState.personalFilters.catalogNotificationStores.filter(
      (store) => !serverStores.has(store),
    ),
  ).filter((store) => stores.includes(store));
  return {
    ...serverState,
    shoppingItems: [...items.values()].slice(0, MAX_SYNCED_SHOPPING_ITEMS),
    personalFilters: {
      stores,
      categoryIds: uniqueValues(
        serverState.personalFilters.categoryIds,
        localState.personalFilters.categoryIds,
      ),
      catalogNotificationStores,
    },
    locale: serverState.locale ?? localState.locale,
    fuelVisited: serverState.fuelVisited || localState.fuelVisited,
    storeSavePromptNextEligibleAt: Math.max(
      serverState.storeSavePromptNextEligibleAt,
      localState.storeSavePromptNextEligibleAt,
    ),
  };
}

export function shoppingMutation(
  previous: ShoppingListItem[],
  next: ShoppingListItem[],
): ShoppingStateMutation | null {
  const previousByKey = new Map(previous.map((item) => [item.key, item] as const));
  const nextByKey = new Map(next.map((item) => [item.key, item] as const));
  const remove = [...previousByKey.keys()].filter((key) => !nextByKey.has(key));
  const upsert = [...nextByKey.values()].filter((item) => {
    const oldItem = previousByKey.get(item.key);
    return !oldItem || serializeShoppingList([oldItem]) !== serializeShoppingList([item]);
  });
  return upsert.length || remove.length ? { upsert, remove } : null;
}

export function mergeUserStateMutations(
  earlier: UserStateMutation | null,
  later: UserStateMutation | null,
): UserStateMutation | null {
  if (!earlier) {
    return later ? normalizeUserStateMutation(later) : null;
  }
  if (!later) {
    return normalizeUserStateMutation(earlier);
  }
  const upserts = new Map<string, ShoppingListItem>();
  const removals = new Set<string>();
  for (const mutation of [earlier.shopping, later.shopping]) {
    for (const key of mutation?.remove ?? []) {
      upserts.delete(key);
      removals.add(key);
    }
    for (const item of mutation?.upsert ?? []) {
      removals.delete(item.key);
      upserts.set(item.key, item);
    }
  }
  const result: UserStateMutation = {
    shopping:
      upserts.size || removals.size
        ? { upsert: [...upserts.values()], remove: [...removals] }
        : undefined,
    personalFilters: {
      ...earlier.personalFilters,
      ...later.personalFilters,
    },
    personalFilterModes: {
      ...earlier.personalFilterModes,
      ...later.personalFilterModes,
    },
    filterSelection: {
      ...earlier.filterSelection,
      ...later.filterSelection,
    },
    gridMode: later.gridMode ?? earlier.gridMode,
    sort: later.sort ?? earlier.sort,
    locale: later.locale ?? earlier.locale,
    themePreference: later.themePreference ?? earlier.themePreference,
    fuelVisited: earlier.fuelVisited || later.fuelVisited || undefined,
    priceWatchDefaultStores: later.priceWatchDefaultStores ?? earlier.priceWatchDefaultStores,
    storeSavePromptNextEligibleAt:
      earlier.storeSavePromptNextEligibleAt === undefined &&
      later.storeSavePromptNextEligibleAt === undefined
        ? undefined
        : Math.max(
            earlier.storeSavePromptNextEligibleAt ?? 0,
            later.storeSavePromptNextEligibleAt ?? 0,
          ),
  };
  if (!Object.keys(result.personalFilters ?? {}).length) {
    delete result.personalFilters;
  }
  if (!Object.keys(result.personalFilterModes ?? {}).length) {
    delete result.personalFilterModes;
  }
  if (!Object.keys(result.filterSelection ?? {}).length) {
    delete result.filterSelection;
  }
  return normalizeUserStateMutation(result);
}
